import React, { useEffect } from "react";
import { useState } from "react";
import { Button, Title, notification, Icon, Card } from "components/common";
import { Switch, Select, Checkbox, Row, Col, Divider } from "antd";
import styles from "./index.module.less";
import { useUpdateAccountDetailsMutation } from "./api";

const { Option } = Select;

const SelectWithCheckbox = ({ settingsData, setPriviliges }) => {
  const [selectedOptions, setSelectedOptions] = useState(
    settingsData?.activities_to_capture?.length
      ? settingsData?.activities_to_capture
      : []
  );

  const [isAllSelected, setAllSelected] = useState(false);

  const options = [
    { label: "New Page", value: "newPage" },
    { label: "Compared Pages", value: "comparedPages" },
    { label: "Study Session", value: "studySession" },
    { label: "Graphs", value: "graphs" },
  ];

  const handleOptionChange = (selected) => {
    setAllSelected(selected.length === options.length);
    const arr = selected.filter((option) => option !== "selectAll");
    setSelectedOptions(arr);
    setPriviliges(arr);
  };

  useEffect(() => {
    if (isAllSelected) {
      const arr = options.map((option) => option.label);
      handleOptionChange(arr);
    } else if (!isAllSelected && selectedOptions.length === options.length) {
      setSelectedOptions([]);
    }
  }, [isAllSelected]);

  return (
    <Select
      mode="multiple"
      style={{ width: "100%" }}
      value={selectedOptions}
      onChange={handleOptionChange}
    >
      <Option key="selectAll">
        <Checkbox
          style={{ gap: "10px" }}
          checked={selectedOptions.length === options.length || isAllSelected}
          onChange={() => setAllSelected(!isAllSelected)}
        />
        Select All
      </Option>
      {options.map((option) => (
        <Option key={option.label} value={option.label}>
          <Checkbox
            style={{ gap: "10px" }}
            checked={selectedOptions.includes(option.label)}
          />
          {option.label}
        </Option>
      ))}
    </Select>
  );
};

const Settings = ({
  settingsData,
  userCode,
  prepareSettingsData,
  setIsDataUpdated,
}) => {
  const [emailNotification, setEmailNotification] = useState(
    settingsData.is_email_enabled
  );
  const [twoFactorAuth, setTwoFactorAuth] = useState(
    settingsData.is_two_factor_auth_enabled
  );
  const [paymentMode, setPaymentMode] = useState(
    settingsData.is_payment_enabled
  );
  const [userRole, setUserRole] = useState(settingsData?.user_role);
  const [priviliges, setPriviliges] = useState([]);
  const [updateAccountDetails, {}] = useUpdateAccountDetailsMutation();

  const handleUpdate = async () => {
    setIsDataUpdated(false);
    const payload = {
      is_two_factor_auth_enabled: twoFactorAuth,
      activities_to_capture: priviliges,
      is_payment_enabled: paymentMode,
      is_email_enabled: emailNotification,
      is_alert_preference: false,
      is_follow_request: false,
      user_code_v2: userCode,
      user_role: userRole,
    };
    const res = await updateAccountDetails({
      body: payload,
    });

    setIsDataUpdated(true);
    if (res) {
      if (res?.error) {
        notification.error({ message: res?.error });
      } else {
        prepareSettingsData(payload);
        notification.success({ message: "Updated Successfully" });
      }
    }
  };

  return (
    <div className={styles.settingsContainer}>
      <Title level={2} style={{ marginBottom: "32px", color: "#1a1a1a" }}>
        Account Settings
      </Title>
      
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card className={styles.settingsCard}>
            <div className={styles.cardHeader}>
              <Icon icon="FiBell" size={20} style={{ color: "#4b93ff" }} />
              <Title level={4} style={{ margin: 0 }}>Notification Preferences</Title>
            </div>
            <div className={styles.settingsGrid}>
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <Icon icon="FiMail" size={18} style={{ color: "#64748b" }} />
                  <div>
                    <div className={styles.settingTitle}>Email Notifications</div>
                    <div className={styles.settingDescription}>Receive updates via email</div>
                  </div>
                </div>
                <Switch
                  checked={emailNotification}
                  onChange={setEmailNotification}
                  style={{ backgroundColor: emailNotification ? "#4b93ff" : "#d1d5db" }}
                />
              </div>
              
              <Divider style={{ margin: "16px 0" }} />
              
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <Icon icon="FiShield" size={18} style={{ color: "#64748b" }} />
                  <div>
                    <div className={styles.settingTitle}>Two-Factor Authentication</div>
                    <div className={styles.settingDescription}>Add an extra layer of security</div>
                  </div>
                </div>
                <Switch
                  checked={twoFactorAuth}
                  onChange={setTwoFactorAuth}
                  style={{ backgroundColor: twoFactorAuth ? "#4b93ff" : "#d1d5db" }}
                />
              </div>
              
              <Divider style={{ margin: "16px 0" }} />
              
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <Icon icon="FiCreditCard" size={18} style={{ color: "#64748b" }} />
                  <div>
                    <div className={styles.settingTitle}>Payment Mode</div>
                    <div className={styles.settingDescription}>Enable payment features</div>
                  </div>
                </div>
                <Switch
                  checked={paymentMode}
                  onChange={setPaymentMode}
                  style={{ backgroundColor: paymentMode ? "#4b93ff" : "#d1d5db" }}
                />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col span={24}>
          <Card className={styles.settingsCard}>
            <div className={styles.cardHeader}>
              <Icon icon="FiActivity" size={20} style={{ color: "#4b93ff" }} />
              <Title level={4} style={{ margin: 0 }}>Activity Tracking</Title>
            </div>
            <div style={{ marginTop: "20px" }}>
              <SelectWithCheckbox 
                settingsData={settingsData} 
                setPriviliges={setPriviliges} 
              />
            </div>
          </Card>
        </Col>
      </Row>
      
      <div style={{ marginTop: "32px", textAlign: "center" }}>
        <Button
          type="primary"
          size="large"
          onClick={handleUpdate}
          style={{
            minWidth: "200px",
            height: "48px",
            borderRadius: "8px",
            fontWeight: "600"
          }}
        >
          <Icon icon="FiSave" size={16} style={{ marginRight: "8px" }} />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default Settings;
